import { useAuth } from '@/contexts/AuthContext';
import { User, Building2, Phone, Globe, Shield } from 'lucide-react';

const InfoRow = ({ label, value, icon: Icon }) => (
  <div className="flex items-center gap-4 py-3.5 border-b border-slate-100 last:border-0">
    <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-slate-500" />
    </div>
    <div>
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-medium text-slate-900 mt-0.5">{value || '—'}</p>
    </div>
  </div>
);

const PlatformBadge = ({ label, enabled, color }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${enabled ? `${color} border-current/20` : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
    <div className={`w-2 h-2 rounded-full ${enabled ? 'bg-current' : 'bg-slate-300'}`} />
    <span className="text-xs font-semibold">{label}</span>
    <span className="text-xs ml-auto">{enabled ? 'Active' : 'Inactive'}</span>
  </div>
);

export default function Profile() {
  const { user } = useAuth();
  const pa = user?.platform_access || {};

  return (
    <div className="space-y-6 max-w-2xl" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Profile</h1>
        <p className="text-slate-500 text-sm mt-0.5">Your account information</p>
      </div>

      {/* Avatar */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 rounded-2xl bg-slate-900 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xl font-bold font-heading">
              {(user?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </span>
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-900">{user?.name}</h2>
            <p className="text-slate-500 text-sm">{user?.email}</p>
            <span className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-100 text-slate-600 px-2.5 py-0.5 rounded-full mt-1.5">
              <Shield className="w-3 h-3" /> Customer
            </span>
          </div>
        </div>
      </div>

      {/* Account Details */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-heading text-base font-semibold text-slate-900 mb-2">Account Details</h3>
        <InfoRow label="Full Name" value={user?.name} icon={User} />
        <InfoRow label="Company" value={user?.company_name} icon={Building2} />
        <InfoRow label="Phone" value={user?.phone} icon={Phone} />
        <InfoRow label="Country" value={user?.country} icon={Globe} />
      </div>

      {/* Platform Access */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-heading text-base font-semibold text-slate-900 mb-4">Platform Access</h3>
        <div className="space-y-2">
          <PlatformBadge label="DeMaxtore" enabled={pa.demaxtore} color="text-sky-600 bg-sky-50" />
          <PlatformBadge label="CommodityBid" enabled={pa.commoditybid} color="text-orange-600 bg-orange-50" />
          <PlatformBadge label="FreightIQ" enabled={pa.freightiq} color="text-green-600 bg-green-50" />
        </div>
      </div>
    </div>
  );
}
